import type { Task } from '@/types'

const DAY_NAMES = ['日', '月', '火', '水', '木', '金', '土']

export function formatNextScheduled(
  nextScheduledAt: string | null,
  scheduledTime: string | null = null,
): string | null {
  if (!nextScheduledAt) return null

  const next = new Date(nextScheduledAt)
  const todayStart = new Date()
  todayStart.setHours(0, 0, 0, 0)

  const nextStart = new Date(next.getFullYear(), next.getMonth(), next.getDate())
  const diffDays = Math.round((nextStart.getTime() - todayStart.getTime()) / 86400000)

  const timeStr = scheduledTime ? ` ${formatScheduledTime(scheduledTime)}` : ''
  const dow = DAY_NAMES[next.getDay()]

  if (diffDays <= 0) return `今日${timeStr}`
  if (diffDays === 1) return `明日${timeStr}`
  return `${next.getMonth() + 1}/${next.getDate()}(${dow})${timeStr}`
}

export function formatScheduledTime(scheduledTime: string | null): string | null {
  if (!scheduledTime) return null
  return scheduledTime.slice(0, 5)
}

function toSortTimestamp(task: Task, now: Date): number {
  const baseDate = task.next_scheduled_at ? new Date(task.next_scheduled_at) : now

  if (task.scheduled_time) {
    const [h, m, s] = task.scheduled_time.split(':').map(Number)
    return new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), h, m, s ?? 0).getTime()
  }

  if (task.next_scheduled_at) {
    return baseDate.getTime()
  }

  return Infinity
}

export function formatDueRemaining(dueDate: string): string | null {
  const due = new Date(dueDate + 'T23:59:59')
  const diffMs = due.getTime() - Date.now()
  if (diffMs <= 0) return '期限超過'
  if (diffMs > 24 * 60 * 60 * 1000) return null
  const totalMin = Math.floor(diffMs / 60000)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h === 0) return `残り ${m}分`
  return m === 0 ? `残り ${h}時間` : `残り ${h}時間${m}分`
}

function dueSoonTimestamp(task: Task): number {
  if (!task.due_date || !task.is_completable) return Infinity
  const due = new Date(task.due_date + 'T00:00:00')
  const now = new Date()
  const diffMs = due.getTime() - now.getTime()
  return diffMs <= 24 * 60 * 60 * 1000 ? due.getTime() : Infinity
}

export function sortTasks(tasks: Task[]): Task[] {
  const now = new Date()
  return [...tasks].sort((a, b) => {
    const aDue = dueSoonTimestamp(a)
    const bDue = dueSoonTimestamp(b)

    // 期限1日以内のタスクを先頭に（期限の早い順）
    if (aDue !== bDue) {
      if (aDue !== Infinity && bDue === Infinity) return -1
      if (aDue === Infinity && bDue !== Infinity) return 1
      return aDue - bDue
    }

    // 完了可能タスクを先に
    if (a.is_completable !== b.is_completable) {
      return a.is_completable ? -1 : 1
    }

    return toSortTimestamp(a, now) - toSortTimestamp(b, now)
  })
}
